import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import {
  Analytics,
  Schedule,
  Message,
  Person,
  TrendingUp,
  AccessTime,
  Reply,
  Visibility,
  Star,
  CheckCircle,
  Assignment,
  Label as LabelIcon
} from '@mui/icons-material';
import { conversationsAPI } from '../../services/api';

interface ConversationMetricsProps {
  open: boolean;
  onClose: () => void;
  conversationId: number;
  conversation: any;
}

interface Metrics {
  total_messages: number;
  agent_messages: number;
  contact_messages: number;
  avg_response_time: number;
  first_response_time: number;
  resolution_time: number;
  conversation_duration: number;
  handover_count: number;
  label_changes: number;
  status_changes: number;
  participant_count: number;
  activity_timeline: ActivityEvent[];
}

interface ActivityEvent {
  id: string;
  type: 'message' | 'assignment' | 'status_change' | 'label_change' | 'participant_change';
  description: string;
  timestamp: string;
  user?: {
    name: string;
    avatar_url?: string;
  };
}

const ConversationMetrics: React.FC<ConversationMetricsProps> = ({
  open,
  onClose,
  conversationId,
  conversation
}) => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (open && conversationId) {
      loadMetrics();
    }
  }, [open, conversationId]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await conversationsAPI.getMetrics(conversationId);
      setMetrics(response);
    } catch (error) {
      console.error('Failed to load metrics:', error);
      // Generate mock data for demo
      setMetrics(generateMockMetrics());
    } finally {
      setLoading(false);
    }
  };

  const generateMockMetrics = (): Metrics => {
    const createdAt = new Date(conversation.created_at);
    const now = new Date();
    const duration = Math.floor((now.getTime() - createdAt.getTime()) / 1000 / 60); // minutes

    return {
      total_messages: Math.floor(Math.random() * 20) + 5,
      agent_messages: Math.floor(Math.random() * 10) + 3,
      contact_messages: Math.floor(Math.random() * 10) + 2,
      avg_response_time: Math.floor(Math.random() * 120) + 30, // seconds
      first_response_time: Math.floor(Math.random() * 300) + 60, // seconds
      resolution_time: conversation.status === 'resolved' ? Math.floor(Math.random() * 1440) + 60 : 0, // minutes
      conversation_duration: duration,
      handover_count: Math.floor(Math.random() * 3),
      label_changes: Math.floor(Math.random() * 5),
      status_changes: Math.floor(Math.random() * 4) + 1,
      participant_count: Math.floor(Math.random() * 3) + 1,
      activity_timeline: generateMockActivityTimeline()
    };
  };

  const generateMockActivityTimeline = (): ActivityEvent[] => {
    const events: ActivityEvent[] = [
      {
        id: '1',
        type: 'message',
        description: 'Conversation started by contact',
        timestamp: conversation.created_at,
      },
      {
        id: '2',
        type: 'assignment',
        description: 'Assigned to support team',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        user: { name: 'System' }
      },
      {
        id: '3',
        type: 'message',
        description: 'First agent response',
        timestamp: new Date(Date.now() - 3000000).toISOString(),
        user: { name: 'Agent Smith' }
      },
      {
        id: '4',
        type: 'status_change',
        description: 'Status changed to pending',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        user: { name: 'Agent Smith' }
      },
      {
        id: '5',
        type: 'label_change',
        description: 'Added label: bug',
        timestamp: new Date(Date.now() - 900000).toISOString(),
        user: { name: 'Agent Smith' }
      }
    ];

    if (conversation.status === 'resolved') {
      events.push({
        id: '6',
        type: 'status_change',
        description: 'Conversation resolved',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        user: { name: 'Agent Smith' }
      });
    }

    return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) return `${hours}h ${remainingMinutes}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'message': return <Message />;
      case 'assignment': return <Assignment />;
      case 'status_change': return <CheckCircle />;
      case 'label_change': return <LabelIcon />;
      case 'participant_change': return <Person />;
      default: return <Analytics />;
    }
  };

  const formatEventTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Analytics />
          Conversation Analytics
          <Chip
            size="small"
            label={`#${conversation.display_id}`}
            color="primary"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              Loading metrics...
            </Typography>
          </Box>
        ) : (
          <>
            <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
              <Tab label="Overview" />
              <Tab label="Timeline" />
            </Tabs>

            {selectedTab === 0 && metrics && (
              <Grid container spacing={3}>
                {/* Message Stats */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Message sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Message Statistics
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2">Total Messages</Typography>
                          <Typography variant="h6">{metrics.total_messages}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2">Agent Messages</Typography>
                          <Typography variant="body1">{metrics.agent_messages}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Contact Messages</Typography>
                          <Typography variant="body1">{metrics.contact_messages}</Typography>
                        </Box>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(metrics.agent_messages / metrics.total_messages) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {Math.round((metrics.agent_messages / metrics.total_messages) * 100)}% from agents
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Response Times */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Response Times
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2">First Response</Typography>
                        <Chip 
                          size="small" 
                          label={formatTime(metrics.first_response_time)}
                          color={metrics.first_response_time < 300 ? 'success' : metrics.first_response_time < 600 ? 'warning' : 'error'}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2">Avg Response</Typography>
                        <Chip 
                          size="small" 
                          label={formatTime(metrics.avg_response_time)}
                          color={metrics.avg_response_time < 120 ? 'success' : metrics.avg_response_time < 300 ? 'warning' : 'error'}
                        />
                      </Box>
                      {metrics.resolution_time > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Resolution Time</Typography>
                          <Chip 
                            size="small" 
                            label={formatDuration(metrics.resolution_time)}
                            color="info"
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Conversation Stats */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Conversation Stats
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Duration</Typography>
                        <Typography variant="body1">{formatDuration(metrics.conversation_duration)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Handovers</Typography>
                        <Typography variant="body1">{metrics.handover_count}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Status Changes</Typography>
                        <Typography variant="body1">{metrics.status_changes}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">Label Changes</Typography>
                        <Typography variant="body1">{metrics.label_changes}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Current Status */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Current Status
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2">Status</Typography>
                        <Chip 
                          size="small" 
                          label={conversation.status}
                          color={conversation.status === 'resolved' ? 'success' : conversation.status === 'pending' ? 'warning' : 'info'}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2">Priority</Typography>
                        <Chip 
                          size="small" 
                          label={conversation.priority}
                          color={conversation.priority === 'urgent' ? 'error' : conversation.priority === 'high' ? 'warning' : 'default'}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">Participants</Typography>
                        <Typography variant="body1">{metrics.participant_count}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {selectedTab === 1 && metrics && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Activity Timeline
                </Typography>
                <List>
                  {metrics.activity_timeline.map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem>
                        <ListItemIcon>
                          {getEventIcon(event.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={event.description}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="caption">
                                {formatEventTime(event.timestamp)}
                              </Typography>
                              {event.user && (
                                <Chip 
                                  size="small" 
                                  label={event.user.name}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < metrics.activity_timeline.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationMetrics;